import React, { useRef, useState, useEffect, useCallback } from "react";
import {
  SafeAreaView,
  View,
  Text,
  Image,
  Dimensions,
  StyleSheet,
  TextInput,
  ScrollView,
  Pressable,
  FlatList,
  TouchableWithoutFeedback,
  KeyboardAvoidingView,
  Keyboard,
  findNodeHandle,
  LayoutAnimation,
  UIManager,
} from "react-native";
import type { View as ViewType } from "react-native";
import { AntDesign } from "@expo/vector-icons";
import { getManufacturerInfoForCharger } from "../services/charger-service";
import { notificationService } from "../services/notification-service";
import { isSuccessful } from "../services/api-requests";
import type { RootStackParamList } from "../navigation/stackTypes";
import { NativeStackScreenProps } from "@react-navigation/native-stack";
import { ChargerManufacturerInfo } from "../models/charger-model";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { Platform } from "react-native";
import LoadingButton from "../components/LoadingButton";
import Header from "../components/Header";
// @ts-ignore - Platform-specific file (.native/.web)
import SerialCameraAddon from "../components/ocr/SerialCameraAddon";
import { getManufacturers } from "../services/supported-chargers-service";
import metadata from "../build-metadata.json";

const { width, height } = Dimensions.get("window");

type ChargerValidationNavigationProps = NativeStackScreenProps<
  RootStackParamList,
  "ChargerValidation"
>;

const ChargerValidation: React.FC<ChargerValidationNavigationProps> = ({
  navigation,
  route,
}) => {
  const [manufacturer, setManufacturer] = useState<string>("");
  const [manufacturerInput, setManufacturerInput] = useState<string>("");
  const [serial, setSerial] = useState("");
  const [isDropdownVisible, setIsDropdownVisible] = useState<boolean>(false);
  const [dropdownTop, setDropdownTop] = useState(0);
  const [dropdownLeft, setDropdownLeft] = useState(0);
  const [serialFocused, setSerialFocused] = useState(false);
  const [manufacturerFocused, setManufacturerFocused] = useState(false);
  const [manufacturers, setManufacturers] = useState<string[]>(["Emporia", "Virtual"]);
  const [filteredManufacturers, setFilteredManufacturers] = useState<string[]>([]);
  const [isLoadingManufacturers, setIsLoadingManufacturers] = useState(true);
  const [keyboardHeight, setKeyboardHeight] = useState(0);
  const [isKeyboardVisible, setIsKeyboardVisible] = useState(false);

  const dropdownButtonRef = useRef<ViewType>(null);
  const scrollViewRef = useRef<any>(null);
  const manufacturerInputRef = useRef<TextInput>(null);
  const manufacturerContainerRef = useRef<ViewType>(null);

  // Fetch manufacturers from server
  useEffect(() => {
    const fetchManufacturers = async () => {
      try {
        setIsLoadingManufacturers(true);
        const response = await getManufacturers();
        
        console.log("Manufacturers API Response:", response);
        
        if (response && isSuccessful(response.status) && response.payload) {
          // The backend returns { success, count, data: [...] }
          // but fetchDataFromAPITyped wraps it, so response.payload IS the whole response
          const manufacturersData = response.payload.data;
          
          if (manufacturersData && Array.isArray(manufacturersData)) {
            let manufacturersList = [...manufacturersData];
            
            console.log("Fetched manufacturers:", manufacturersList);
            
            // Add 'Virtual' for non-production environments
            const buildBranch = metadata.buildBranch;
            const isProduction = buildBranch === 'production';
            
            if (!isProduction && !manufacturersList.includes('Virtual')) {
              manufacturersList.push('Virtual');
            }
            
            setManufacturers(manufacturersList);
            setFilteredManufacturers(manufacturersList);
          } else {
            console.warn("Invalid manufacturers data format:", response.payload);
            // Keep default list
          }
        } else {
          console.warn("API call failed:", response);
          // Fallback to default list on error
          notificationService.sendNotification(
            "warning",
            "Could not load manufacturer list. Using defaults."
          );
        }
      } catch (error: any) {
        console.error("Error fetching manufacturers:", error);
        notificationService.sendNotification(
          "warning",
          "Could not load manufacturer list. Using defaults."
        );
      } finally {
        setIsLoadingManufacturers(false);
      }
    };

    fetchManufacturers();
  }, []);

  // Filter manufacturers based on input
  useEffect(() => {
    if (manufacturerInput.trim() === "") {
      setFilteredManufacturers(manufacturers);
    } else {
      const filtered = manufacturers.filter((m) =>
        m.toLowerCase().includes(manufacturerInput.toLowerCase())
      );
      setFilteredManufacturers(filtered);
    }
  }, [manufacturerInput, manufacturers]);

  // Scroll to bottom on component mount
  useEffect(() => {
    const timer = setTimeout(() => {
      scrollViewRef.current?.scrollToEnd({ animated: true });
    }, 300);
    
    return () => clearTimeout(timer);
  }, []);

  // Enable LayoutAnimation on Android
  useEffect(() => {
    if (Platform.OS === 'android' && UIManager.setLayoutAnimationEnabledExperimental) {
      UIManager.setLayoutAnimationEnabledExperimental(true);
    }
  }, []);

  // Track keyboard visibility and height
  useEffect(() => {
    const keyboardWillShowListener = Keyboard.addListener(
      Platform.OS === 'ios' ? 'keyboardWillShow' : 'keyboardDidShow',
      (e) => {
        setKeyboardHeight(e.endCoordinates.height);
        setIsKeyboardVisible(true);
      }
    );
    const keyboardWillHideListener = Keyboard.addListener(
      Platform.OS === 'ios' ? 'keyboardWillHide' : 'keyboardDidHide',
      () => {
        setKeyboardHeight(0);
        setIsKeyboardVisible(false);
      }
    );

    return () => {
      keyboardWillShowListener.remove();
      keyboardWillHideListener.remove();
    };
  }, []);

  // Scroll to manufacturer input when focused and keyboard is visible
  const scrollToManufacturerInput = useCallback(() => {
    if (Platform.OS !== 'web' && manufacturerContainerRef.current && scrollViewRef.current) {
      // Measure the manufacturer container position relative to the ScrollView
      manufacturerContainerRef.current.measureLayout(
        findNodeHandle(scrollViewRef.current) as any,
        (x, y, width, height) => {
          // Scroll so the manufacturer input is near the top of the visible area
          // with some padding for the header
          const scrollToY = Math.max(0, y - 120);
          scrollViewRef.current?.scrollTo({ y: scrollToY, animated: true });
        },
        () => {
          // Fallback: scroll by a fixed amount
          scrollViewRef.current?.scrollTo({ y: 200, animated: true });
        }
      );
    }
  }, []);

  // Scroll to bottom when serial input is focused to show the button and helper text
  const scrollToSerialInput = useCallback(() => {
    if (Platform.OS !== 'web' && scrollViewRef.current) {
      // Small delay to let the keyboard animation start
      setTimeout(() => {
        scrollViewRef.current?.scrollToEnd({ animated: true });
      }, 100);
    }
  }, []);

  const handleManufacturerSelect = (value: string) => {
    setManufacturer(value);
    setManufacturerInput(value);
    setIsDropdownVisible(false);
    Keyboard.dismiss();
  };

  const handleManufacturerInputChange = (text: string) => {
    setManufacturerInput(text);
    
    // Check if the typed text exactly matches a manufacturer
    const exactMatch = manufacturers.find(
      (m) => m.toLowerCase() === text.toLowerCase()
    );
    
    if (exactMatch) {
      setManufacturer(exactMatch);
    } else {
      // Clear the selected manufacturer if input doesn't match
      setManufacturer("");
    }
    
    // Show dropdown when user types (if not already visible)
    if (!isDropdownVisible && text.length > 0) {
      updateDropdownPosition();
    }
  };

  const handleManufacturerFocus = () => {
    setManufacturerFocused(true);
    // Small delay to ensure input is rendered and positioned
    setTimeout(() => {
      updateDropdownPosition();
      // Scroll to bring the manufacturer input into view above the keyboard
      scrollToManufacturerInput();
    }, 150);
  };

  const handleManufacturerBlur = () => {
    setManufacturerFocused(false);
    // Delay closing to allow click on dropdown items
    setTimeout(() => {
      setIsDropdownVisible(false);
    }, 200);
  };

  const updateDropdownPosition = () => {
    const anchor = findNodeHandle(dropdownButtonRef.current);

    if (
      typeof dropdownButtonRef.current?.measure === "function" &&
      anchor != null
    ) {
      dropdownButtonRef.current.measure((x, y, width, height, pageX, pageY) => {
        setDropdownTop(pageY + height);
        setDropdownLeft(pageX);
        setIsDropdownVisible(true);
      });
    } else {
      setIsDropdownVisible(true);
    }
  };

  //validate charger before moving to the next step
  const validateSerial = async () => {
    if (serial === "") {
      notificationService.sendNotification(
        "error",
        "Please enter a serial number"
      );
      return;
    }
    
    if (manufacturer === "" || !manufacturers.includes(manufacturer)) {
      notificationService.sendNotification(
        "error",
        "Please select a valid manufacturer from the list"
      );
      return;
    }
    
    const infoResponse = await getManufacturerInfoForCharger(
      serial,
      manufacturer
    );

    if (
      !infoResponse ||
      !isSuccessful(infoResponse.status) ||
      !infoResponse.payload
    ) {
      notificationService.sendNotification(
        "error",
        infoResponse.payload?.message ?? "Error registering your charger"
      );
    } else {
      // Explicitly assert that payload is an array of ChargerManufacturerInfo
      const manufacturerArray: ChargerManufacturerInfo = infoResponse.payload;

      if (!manufacturerArray) {
        notificationService.sendNotification(
          "error",
          "No manufacturer info found"
        );
      }

      let manufacturer;
      if (Array.isArray(manufacturerArray)) {
        manufacturer = manufacturerArray[0];
      } else {
        manufacturer = manufacturerArray;
      }

      const storedUserSession = await AsyncStorage.getItem("userSession");

      let userEmail = null;
      if (storedUserSession) {
        const userSession = JSON.parse(storedUserSession);
        userEmail = userSession.email;
      }

      navigation.navigate("RegisterUpdateCharger", {
        unit_serial: serial,
        capacity: manufacturer.charge_rate_amps,
        name: manufacturer.name,
        manufacturer: manufacturer.manufacturer,
        chargerEmail: userEmail ?? "",
        returnToOnboarding: route.params?.returnToOnboarding ?? false,
      });
    }
  };

  const closeDropdown = () => {
    if (isDropdownVisible) setIsDropdownVisible(false);
    Keyboard.dismiss();
  };

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: "#0B1E1D" }}>
      <KeyboardAvoidingView
        behavior={Platform.OS === "ios" ? "padding" : undefined}
        style={{ flex: 1 }}
      >
        <TouchableWithoutFeedback
          onPress={(e: React.TouchEvent | any) => {
            const target = e.nativeEvent.target;
            if (
              target?.tagName !== "INPUT" &&
              target?.getAttribute?.("role") !== "textbox"
            ) {
              closeDropdown();
            }
          }}
          accessible={false}
          disabled={!isDropdownVisible}
        >
          <View style={{ flex: 1 }}>
            <View style={[styles.containerBgColor, { flex: 1 }]}>
              {Platform.OS !== "web" && (
                <Header headerText="Register Charger" />
              )}

              <ScrollView
                ref={scrollViewRef}
                contentContainerStyle={{ paddingBottom: 20 }}
                keyboardShouldPersistTaps="always"
                onScrollBeginDrag={() => {
                  setIsDropdownVisible(false);
                }}
                onScrollEndDrag={() => {}}
              >
                <Image
                  source={require("../../assets/ChargerStationBg.png")}
                  style={
                    Platform.OS === "web"
                      ? {
                          aspectRatio: 1,
                        }
                      : {
                          width: "100%",
                          height: 250,
                        }
                  }
                  alt="Background Image"
                />
                <View className="px-6">
                  <Text className="mb-1 text-lg font-semibold text-white">
                    My Chargers
                  </Text>
                  <Text className="mb-4 text-sm font-medium text-white">
                    Register your first charger by entering the charger's serial
                    number below:
                  </Text>

                  <View style={styles.inputRow} ref={manufacturerContainerRef}>
                    <View style={styles.imageContainer}>
                      <Image
                        source={require("../../assets/stationPlugIn.png")}
                        resizeMode="cover"
                        style={styles.image}
                      />
                    </View>

                    <View style={styles.inputColumn}>
                      <View style={styles.labelContainer}>
                        <Text className="text-base font-semibold text-white">
                          Manufacturer {isLoadingManufacturers && "(Loading...)"}
                        </Text>
                        {manufacturerInput && !manufacturer && (
                          <Text className="ml-2 text-xs text-red-400">
                            (Must select from list)
                          </Text>
                        )}
                      </View>
                      <View
                        style={[
                          styles.manufacturerInputContainer,
                          manufacturerFocused && styles.inputFocused,
                          manufacturerInput && !manufacturer && styles.inputInvalid,
                        ]}
                        ref={dropdownButtonRef}
                      >
                        <TextInput
                          ref={manufacturerInputRef}
                          value={manufacturerInput}
                          onChangeText={handleManufacturerInputChange}
                          onFocus={handleManufacturerFocus}
                          onBlur={handleManufacturerBlur}
                          style={[
                            styles.manufacturerInput,
                            manufacturerInput && !manufacturer && { color: "#ff6b6b" }
                          ]}
                          placeholder="Type or select manufacturer..."
                          placeholderTextColor="#999"
                          editable={!isLoadingManufacturers}
                          autoCapitalize="words"
                          autoCorrect={false}
                          className={
                            Platform.OS === "web"
                              ? "p-3 rounded-full"
                              : "p-1 rounded-full"
                          }
                        />
                        {manufacturerInput.length > 0 && (
                          <Pressable
                            onPress={() => {
                              setManufacturerInput("");
                              setManufacturer("");
                            }}
                            style={styles.clearIcon}
                          >
                            <AntDesign name="closecircle" size={16} color="#999" />
                          </Pressable>
                        )}
                        <AntDesign
                          name="search1"
                          size={16}
                          color="#999"
                          style={styles.searchIcon}
                        />
                      </View>

                      {/* Inline dropdown for mobile - scrolls with content and stays visible above keyboard */}
                      {Platform.OS !== "web" && isDropdownVisible && filteredManufacturers.length > 0 && (
                        <View style={styles.inlineDropdown}>
                          <ScrollView
                            style={{ maxHeight: isKeyboardVisible ? 180 : 200 }}
                            keyboardShouldPersistTaps="always"
                            nestedScrollEnabled={true}
                          >
                            {filteredManufacturers.map((item) => {
                              const isSelected = item === manufacturer;
                              const inputLower = manufacturerInput.toLowerCase();
                              const itemLower = item.toLowerCase();
                              const matchIndex = itemLower.indexOf(inputLower);

                              return (
                                <Pressable
                                  key={item}
                                  onPress={() => handleManufacturerSelect(item)}
                                  style={({ pressed }) => [
                                    styles.autocompleteItem,
                                    pressed && styles.autocompleteItemPressed,
                                    isSelected && styles.autocompleteItemSelected,
                                  ]}
                                >
                                  <View style={styles.autocompleteItemContent}>
                                    <Text style={styles.autocompleteItemText}>
                                      {matchIndex >= 0 && manufacturerInput.length > 0 ? (
                                        <>
                                          {item.substring(0, matchIndex)}
                                          <Text style={styles.highlightedText}>
                                            {item.substring(matchIndex, matchIndex + manufacturerInput.length)}
                                          </Text>
                                          {item.substring(matchIndex + manufacturerInput.length)}
                                        </>
                                      ) : (
                                        item
                                      )}
                                    </Text>
                                    {isSelected && (
                                      <AntDesign
                                        name="check"
                                        size={16}
                                        color="#39BEB7"
                                      />
                                    )}
                                  </View>
                                </Pressable>
                              );
                            })}
                          </ScrollView>
                          <View style={styles.autocompleteFooter}>
                            <Text style={styles.autocompleteFooterText}>
                              {filteredManufacturers.length} manufacturer{filteredManufacturers.length !== 1 ? 's' : ''} found
                            </Text>
                          </View>
                        </View>
                      )}

                      <Text className="text-base font-semibold text-white">
                        Serial Number
                      </Text>
                      <View style={styles.serialInputContainer}>
                        <TextInput
                          placeholder={serialFocused ? "" : "Enter serial number"}
                          placeholderTextColor={"#fff"}
                          style={[
                            styles.serialInput,
                            serialFocused && {
                              borderWidth: 2,
                              borderColor: "#39BEB7",
                            },
                          ]}
                          className={
                            Platform.OS === "web"
                              ? "p-3 text-white rounded-full"
                              : "mt-1 p-1 text-white rounded-full"
                          }
                          value={serial}
                          onFocus={() => {
                            setSerialFocused(true);
                            scrollToSerialInput();
                          }}
                          onBlur={() => setSerialFocused(false)}
                          onChange={(e) => setSerial(e.nativeEvent.text)}
                          clearTextOnFocus={true}
                        />
                        <View style={styles.cameraIcon}>
                          <SerialCameraAddon
                            manufacturer_name={manufacturer}
                            onDetected={(detectedSerial: string) => setSerial(detectedSerial)}
                            singleMode={true}
                            iconColor="#fff"
                          />
                        </View>
                      </View>
                    </View>
                  </View>

                  {/* <Pressable
                  style={{ backgroundColor: serial === "" ? "#636363" : "#39BEB7", zIndex: 1 }}
                  className="p-3 mt-4 rounded-full"
                  onPress={() => validateSerial()}
                > */}
                  <View>
                    <LoadingButton
                      onPress={validateSerial}
                      // className={`p-3 mt-4 rounded-full`}
                      backgroundColor={`${
                        serial === "" ? "#636363" : "#39BEB7"
                      }`}
                      style={{
                        paddingVertical: 0,
                        paddingHorizontal: 0,
                        marginTop: 20,
                        borderRadius: 20,
                      }}
                    >
                      <Text className="text-center text-white">
                        Continue to Registration
                      </Text>
                    </LoadingButton>
                  </View>
                  {/* </Pressable> */}

                  <View>
                    <View style={styles.line} />
                    <View className="flex flex-row gap-2 items-center">
                      <AntDesign
                        name="questioncircle"
                        size={17}
                        color="white"
                      />
                      <Text className="text-sm font-bold text-white">
                        Where can I find my charger's serial number?
                      </Text>
                    </View>
                    <Text className="mt-2 text-sm font-medium text-white">
                      Your charger's serial number is located on one of the
                      sides or on the front of your charger.
                    </Text>
                  </View>
                </View>
              </ScrollView>

              {/* Absolutely positioned dropdown for web only */}
              {Platform.OS === "web" && isDropdownVisible && filteredManufacturers.length > 0 && (
                <View
                  style={[
                    styles.autocompleteDropdown,
                    {
                      left: dropdownLeft,
                      top: dropdownTop + 4,
                    },
                  ]}
                >
                  <ScrollView
                    style={{ maxHeight: 200 }}
                    keyboardShouldPersistTaps="always"
                    nestedScrollEnabled={true}
                  >
                    {filteredManufacturers.map((item) => {
                      const isSelected = item === manufacturer;
                      const inputLower = manufacturerInput.toLowerCase();
                      const itemLower = item.toLowerCase();
                      const matchIndex = itemLower.indexOf(inputLower);

                      return (
                        <Pressable
                          key={item}
                          onPress={() => handleManufacturerSelect(item)}
                          style={({ pressed }) => [
                            styles.autocompleteItem,
                            pressed && styles.autocompleteItemPressed,
                            isSelected && styles.autocompleteItemSelected,
                          ]}
                        >
                          <View style={styles.autocompleteItemContent}>
                            <Text style={styles.autocompleteItemText}>
                              {matchIndex >= 0 && manufacturerInput.length > 0 ? (
                                <>
                                  {item.substring(0, matchIndex)}
                                  <Text style={styles.highlightedText}>
                                    {item.substring(matchIndex, matchIndex + manufacturerInput.length)}
                                  </Text>
                                  {item.substring(matchIndex + manufacturerInput.length)}
                                </>
                              ) : (
                                item
                              )}
                            </Text>
                            {isSelected && (
                              <AntDesign
                                name="check"
                                size={16}
                                color="#39BEB7"
                              />
                            )}
                          </View>
                        </Pressable>
                      );
                    })}
                  </ScrollView>
                  <View style={styles.autocompleteFooter}>
                    <Text style={styles.autocompleteFooterText}>
                      {filteredManufacturers.length} manufacturer{filteredManufacturers.length !== 1 ? 's' : ''} found
                    </Text>
                  </View>
                </View>
              )}
            </View>
          </View>
        </TouchableWithoutFeedback>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  containerBgColor: {
    backgroundColor: "#0B1E1D",
  },
  line: Platform.select({
    web: {
      borderBottomColor: "#636363",
      borderBottomWidth: 1,
      borderStyle: "dashed",
      marginVertical: 20,
    },
    default: {
      borderBottomColor: "#636363",
      borderBottomWidth: 1,
      marginVertical: 20,
    },
  }),
  inputRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 16,
  },
  imageContainer: {
    justifyContent: "center",
    alignItems: "flex-start",
    width: 60,
  },
  image: {
    height: 48,
    width: 38,
  },
  inputColumn: {
    flex: 1,
    justifyContent: "center",
  },
  labelContainer: {
    flexDirection: "row",
    alignItems: "center",
    flexWrap: "wrap",
  },
  manufacturerInputContainer: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#292D33",
    borderRadius: 50,
    marginBottom: 10,
    marginTop: 5,
    position: "relative",
    minHeight: 48,
  },
  inputFocused: {
    borderWidth: 2,
    borderColor: "#39BEB7",
  },
  inputInvalid: {
    borderWidth: 2,
    borderColor: "#ff6b6b",
  },
  manufacturerInput: {
    flex: 1,
    paddingVertical: 12,
    paddingLeft: 24,
    paddingRight: 70,
    color: "#fff",
    backgroundColor: "transparent",
    borderRadius: 50,
    fontSize: 15,
    minHeight: 48,
  },
  searchIcon: {
    position: "absolute",
    right: 15,
  },
  clearIcon: {
    position: "absolute",
    right: 40,
    padding: 5,
  },
  serialInput: {
    backgroundColor: "#292D33",
    paddingVertical: 12,
    paddingLeft: 24,
    paddingRight: 20,
    color: "#fff",
    borderRadius: 50,
    textAlign: "left",
    fontSize: 15,
    minHeight: 48,
    flex: 1,
  },
  serialInputContainer: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#292D33",
    borderRadius: 50,
    minHeight: 48,
  },
  cameraIcon: {
    position: "absolute",
    right: 15,
    justifyContent: "center",
    alignItems: "center",
  },
  autocompleteDropdown: {
    position: "absolute",
    width: Platform.OS === "web" ? "45%" : "58%",
    maxWidth: 500,
    backgroundColor: "#292D33",
    borderRadius: 12,
    zIndex: 9999,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 8,
    borderWidth: 1,
    borderColor: "#39BEB7",
    overflow: "hidden",
  },
  inlineDropdown: {
    backgroundColor: "#292D33",
    borderRadius: 12,
    marginBottom: 10,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 8,
    borderWidth: 1,
    borderColor: "#39BEB7",
    overflow: "hidden",
  },
  autocompleteItem: {
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderBottomWidth: 1,
    borderBottomColor: "#3a3f47",
  },
  autocompleteItemPressed: {
    backgroundColor: "#1f2329",
  },
  autocompleteItemSelected: {
    backgroundColor: "#1a3635",
  },
  autocompleteItemContent: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  autocompleteItemText: {
    color: "#fff",
    fontSize: 15,
    flex: 1,
  },
  highlightedText: {
    fontWeight: "bold",
    color: "#39BEB7",
  },
  autocompleteFooter: {
    paddingVertical: 8,
    paddingHorizontal: 16,
    backgroundColor: "#1f2329",
    borderTopWidth: 1,
    borderTopColor: "#3a3f47",
  },
  autocompleteFooterText: {
    color: "#999",
    fontSize: 12,
    textAlign: "center",
  },
  // Legacy styles (kept for serial input compatibility)
  dropdownContainer: {
    backgroundColor: "#292D33",
    borderRadius: 50,
    marginBottom: 10,
    marginTop: 5,
  },
  dropdownRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  dropdown: {
    padding: 15,
    color: "#fff",
    backgroundColor: "#292D33",
    borderRadius: 50,
    textAlign: "center",
    fontSize: 14,
    fontWeight: "bold",
    flex: 1,
    height: 30,
  },
  dropdownIcon: {
    marginRight: 10,
  },
  dropdownOptionsContainer: {
    backgroundColor: "#292D33",
    borderRadius: 8,
    marginTop: 5,
    marginHorizontal: 150,
    position: "absolute",
    top: 150,
    width: "50%",
    zIndex: 10,
  },
  dropdownOption: {
    padding: 15,
    color: "#fff",
    fontSize: 16,
    borderBottomColor: "#636363",
    borderBottomWidth: 1,
  },
});
export default ChargerValidation;
